<template>
  <form ref="fileUploadForm" >
    <div class="form-inline">
      <!--<label>Upload: </label>-->
      <input type="file"
             enctype="multipart/form-data"
             id="file"
             ref="file" @change.prevent.stop="handleFileUpload"
             class="form-control form-control-file mr-md-2 mr-2"/>
      <input type='hidden' id='file-csrf-id' :name='csrfParam' :value='csrfToken'/>
    </div>
  </form>
</template>
<script>
import router from '@/router/index';
import { mapGetters, mapActions } from 'vuex';
import { INIT_NO_OP_NUM, CDA_FILE_TYPE, PDF_FILE_TYPE, DEVICE_CSV_FILE_TYPE } from '@/config';
import { PRIVILEGES } from '@/constants/privileges';
import { assertPrivilegeOrAlert } from '@/utils/privilegeGate';
import { CdasNavigation,PdfsNavigation, DataUploadNavigation } from '@/store/modules/fsm/messages';
import { scanCdaById, INVALID_CDA_UPLOAD_PARSE_MESSAGE } from '@/api/cdaApi';
import { getAllData } from '@/api/sessionApi';
import { uploadBloodPressureDeviceCsv } from '@/api/deviceApi';
import { h } from 'vue';
export default {
  name: 'file-upload',
  props: {
    fileType: {
      type: String,
      default: ''
    },
  },
  computed: {
    ...mapGetters('login', {
      csrf : 'getCsrf'
    }),
    csrfParam() {
      return this.csrf?.param || ''
    },
    csrfToken() {
      return this.csrf?.token || ''
    },
    ...mapGetters('login/cda', {
      indexOfLastCda: 'getIndexOfLastCda'
    }),
    ...mapGetters('login/pdf', {
      indexOfLastPdf: 'getIndexOfLastPdf'
    }),
    indexOfLast () {
      return (this.fileType === CDA_FILE_TYPE)
      ? this.indexOfLastCda
      : this.indexOfLastPdf === -1 ? -1 : 0
    }

  },
    methods: {
      ...mapActions('login',[
        'updateApplicationModel'
      ]),
      ...mapActions('login/cda', [
        'uploadCda'
      ]),
      ...mapActions('login/pdf', [
        'uploadPdf'
      ]),
      ...mapActions('login/watch',[
        'setExternalMeasuresLoaded'
      ]),
      handleFileUpload () {
        this.updateFileRef();

        if (this.fileType === CDA_FILE_TYPE || this.fileType === PDF_FILE_TYPE) {
          if (!assertPrivilegeOrAlert(
            PRIVILEGES.UPLOAD_DOCUMENTS,
            'You do not have permission to upload documents.'
          )) {
            if (this.$refs.file) this.$refs.file.value = '';
            return;
          }
        }

        let upload = null;
        let contentRouteName = '';
        let actionField = null;
        let loader = null;
        if (this.fileType === CDA_FILE_TYPE) {
          upload = this.uploadCda;
          contentRouteName = CdasNavigation;
          actionField = 'ccda';
        } else if (this.fileType === PDF_FILE_TYPE) {
          upload = this.uploadPdf;
          contentRouteName = PdfsNavigation;
          actionField = 'pdf';
        } else if (this.fileType === DEVICE_CSV_FILE_TYPE) {
          upload = uploadBloodPressureDeviceCsv;
        } else {
          this.$swal({
            type: 'error',
            title: 'Unable to upload',
            text: `No channel to upload file of type '${this.fileType}'`,
          })
        }

        /**
         * send ajax file upload request to server
         */
        if (!this.file) {
          this.$swal({
            type: 'error',
            title: 'Unable to upload',
            text: 'Please try again or choose another file.',
          })
        } else {
          this.$swal({
            title: `Upload ${this.file.name}`,
            text: 'This may take a while. Please do not refresh this page while it is uploading.',
            type: 'warning',
            showCancelButton: true,
            cancelButtonColor: '#d33',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Yes, upload',
            reverseButtons: true,
          }).then((result) => {
            if (result.value) {
              loader = this.$loading.show({
                container: this.$refs.pageBody
              },{
                after: () => h('div',{},`Uploading ${this.fileType}...`)
              });

              let formData = new FormData();
              formData.append('file-field', this.file);
              //TODO: use this to parameterize devices?
              formData.append('action-field', actionField);
              formData.append(this.csrfParam,this.csrfToken);
              
              // dispatch 'upload*' action
              //TODO: use uploadFile from fileApi in the future
              upload(formData)
              .then( (id) => {
                if (this.fileType === CDA_FILE_TYPE) {
                  // update loader text
                  loader.hide();
                  loader = this.$loading.show({
                    container: this.$refs.pageBody
                  },{
                    after: () => h('div',{},`Scanning CDA...`)
                  });

                  return scanCdaById(id);
                }
                return;
              })
              .then(() => {

                // update loader text
                loader.hide();
                loader = this.$loading.show({
                  container: this.$refs.pageBody
                },{
                  after: () => h('div',{},`Updating application data...`)
                });

                if (this.fileType === DEVICE_CSV_FILE_TYPE) {
                  this.setExternalMeasuresLoaded(false);
                  return;
                };
                // Force server PHR rebuild so dropdown can see new CDAs and PDFs;
                // cached getAllData() omits recently uploaded documents.
                const needsFullRebuild =
                  this.fileType === CDA_FILE_TYPE || this.fileType === PDF_FILE_TYPE;
                return this.updateApplicationModel(
                  needsFullRebuild ? { rebuild: true } : {}
                );
                
              })
              .then(()=>{
                if (this.fileType === DEVICE_CSV_FILE_TYPE) {
                  this.$swal({
                    type: 'success',
                    title: 'Device data uploaded',
                    text: 'You can go to the Watch/Vitals pages to view data.'
                  });
                }

                if (!contentRouteName) return;

                if(this.fileType === PDF_FILE_TYPE) {
                  router.push({ name: contentRouteName, params: {selectedPdf:this.indexOfLast} });
                  return;
                }

                if (this.fileType === CDA_FILE_TYPE) {
                  const idx = this.indexOfLast;
                  if (idx >= 0) {
                    router.push({
                      name: contentRouteName,
                      params: { selectedCda: String(idx) },
                    });
                  }
                  this.$swal({
                    type: 'success',
                    title: 'CDA uploaded',
                    text: 'Your document was uploaded successfully.',
                  });
                }
              })
              .catch( (err) => {
                const uploadErrorText =
                  typeof err === 'string' ? err : err?.message || 'Upload failed. Please try again.';
                const isInvalidCcdaParse =
                  this.fileType === CDA_FILE_TYPE &&
                  uploadErrorText === INVALID_CDA_UPLOAD_PARSE_MESSAGE;
                this.$swal({
                  type: 'error',
                  title: isInvalidCcdaParse ? 'CCDA could not be parsed' : 'Unable to upload',
                  text: uploadErrorText,
                });
                console.error(err);
              })
              .finally( () => {
                loader.hide();
                loader = null;
              });
            }
          });

        }


      },

      /**
       * updates reference to local file for the file chooser
       */
      updateFileRef () {
        this.file = this.$refs.file.files[0];
      }
    },
  }
</script>

<style scoped lang="scss">
  .btn {
    font-size: 0.9rem;
  }
  input[type="text"] {
    font-size: 0.9rem;
  }
  input[type="file"] {
    font-size: 12px;
    margin: 0;
    height: 100%;
    /*width: 70%;*/
  }
</style>
